import type { StoreSettingsPayload } from '@jane/business-admin/types';

export type IntegrationKey =
  | 'stronghold'
  | 'moneris'
  | 'canpay'
  | 'canpay_v2'
  | 'canpay_v2remotepay'
  | 'payfirma'
  | 'square';

interface ShouldShowIntegrationOptions {
  isInternal: boolean;
  storePayload?: StoreSettingsPayload;
}

const hasValue = (val: unknown): boolean =>
  typeof val === 'string' ? val.trim().length > 0 : Boolean(val);

const CONFIG_FIELDS: Record<
  IntegrationKey,
  (p: StoreSettingsPayload) => Array<unknown>
> = {
  stronghold: (p) => [
    p.stronghold_integration?.enabled,
    p.stronghold_integration?.secret_key,
    p.stronghold_integration?.publishable_key,
  ],
  moneris: (p) => [
    p.moneris_integration?.enabled,
    p.moneris_integration?.moneris_store_id,
    p.moneris_integration?.moneris_checkout_id,
    p.moneris_integration?.moneris_api_token,
  ],
  canpay: (p) => [
    p.store?.canpay_enabled,
    p.store?.canpay_merchant_id,
    p.store?.canpay_store_code,
    p.store?.canpay_lane_id,
  ],
  canpay_v2: (p) => [
    p.canpay_v2_integration?.enabled,
    p.canpay_v2_integration?.app_key,
    p.canpay_v2_integration?.api_secret,
  ],
  canpay_v2remotepay: (p) => [
    p.canpay_v2remotepay_integration?.enabled,
    p.canpay_v2remotepay_integration?.app_key,
    p.canpay_v2remotepay_integration?.api_secret,
  ],
  payfirma: (p) => [
    p.payfirma_integration?.enabled,
    p.payfirma_integration?.payfirma_custom_id,
    p.payfirma_integration?.payfirma_client_id,
    p.payfirma_integration?.payfirma_client_secret,
  ],
  square: (p) => [
    p.square_integration?.enabled,
    p.square_integration?.location_id,
  ],
};

export const shouldShowIntegration = (
  integration: IntegrationKey,
  { isInternal, storePayload }: ShouldShowIntegrationOptions
): boolean => {
  if (isInternal) return true;
  if (!storePayload) return false;

  return CONFIG_FIELDS[integration](storePayload).some(hasValue);
};
